import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../../lib/api";
import {
  ArrowLeft, Loader2, CheckCircle2,
  AlertTriangle, XCircle, Info, FileArchive,
} from "lucide-react";

// -----------------------------------------------------------------------------
// ImportImagesZip — /admin/importazione/immagini
//
// Bulk image import via ZIP. Filenames must follow the pattern:
//   <slug>_cover.jpg   → cover image
//   <slug>_01.jpg      → gallery position 1
//   <slug>_02.jpg      → gallery position 2
// Two-phase flow (preview → commit), same UX pattern as /admin/importazione.
// -----------------------------------------------------------------------------

const Step = ({ n, title, active, done }) => (
  <div className="flex items-center gap-3">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${done ? "bg-[#12964A] text-white" : active ? "bg-[#F0FAF3] text-[#12964A] border-2 border-[#12964A]" : "bg-slate-100 text-slate-400"}`}>{done ? "✓" : n}</div>
    <div className={`text-sm ${active || done ? "text-[#1F2937] font-medium" : "text-slate-400"}`}>{title}</div>
  </div>
);

const Stat = ({ n, label, color = "slate" }) => {
  const colors = {
    slate: "bg-slate-50 text-slate-700",
    green: "bg-[#F0FAF3] text-[#0E7C3D]",
    yellow: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
    blue: "bg-blue-50 text-blue-700",
  };
  return (
    <div className={`rounded-2xl p-4 ${colors[color] || colors.slate}`}>
      <div className="text-2xl font-bold">{n}</div>
      <div className="text-xs uppercase tracking-[0.15em] mt-1">{label}</div>
    </div>
  );
};

const ImportImagesZip = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState(null);
  const [galleryMode, setGalleryMode] = useState("append"); // append | replace
  const [replaceCover, setReplaceCover] = useState(true);

  const step = result ? 4 : preview ? 3 : file ? 2 : 1;

  const doPreview = async () => {
    if (!file) return;
    setPreviewing(true); setError(null); setResult(null);
    try {
      const r = await adminApi.importImagesZipPreview(file, {
        gallery_mode: galleryMode,
        replace_cover: replaceCover,
      });
      setPreview(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore anteprima ZIP");
    } finally { setPreviewing(false); }
  };

  const doCommit = async () => {
    if (!file || !preview) return;
    setCommitting(true); setError(null);
    try {
      const r = await adminApi.importImagesZipCommit(file, {
        gallery_mode: galleryMode,
        replace_cover: replaceCover,
      });
      setResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore importazione ZIP");
    } finally { setCommitting(false); }
  };

  const reset = () => {
    setFile(null); setPreview(null); setResult(null); setError(null);
  };

  const matchedByBusiness = useMemo(() => {
    if (!preview?.matched) return [];
    const map = new Map();
    for (const m of preview.matched) {
      if (!map.has(m.business_id)) {
        map.set(m.business_id, { business_name: m.business_name, slug: m.slug, items: [] });
      }
      map.get(m.business_id).items.push(m);
    }
    // Sort images within each business: cover first, then gallery by role_num
    for (const g of map.values()) {
      g.items.sort((a, b) => {
        if (a.role === "cover") return -1;
        if (b.role === "cover") return 1;
        return (a.role_num || 0) - (b.role_num || 0);
      });
    }
    return Array.from(map.values()).sort((a, b) => a.business_name.localeCompare(b.business_name));
  }, [preview]);

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="import-images-page">
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/admin/importazione" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#12964A]" data-testid="back-to-import">
            <ArrowLeft className="w-4 h-4" /> Import/Export
          </Link>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Amministrazione</div>
          <h1 className="font-display font-bold text-4xl md:text-5xl text-[#1F2937]">Importa immagini (ZIP)</h1>
          <p className="text-slate-500 mt-2 max-w-2xl">
            Carica un archivio ZIP contenente le foto delle attività. Il sistema le abbinerà
            automaticamente in base al nome file e mostrerà un&apos;anteprima prima di confermare.
          </p>
        </div>

        <section className="bg-white border border-[#E5E7EB] rounded-3xl p-6 md:p-8">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-1">Importazione guidata</div>
          <div className="font-display font-bold text-2xl text-[#1F2937] mb-6">4 semplici passi</div>

          <div className="flex flex-wrap items-center gap-2 md:gap-4 mb-8 pb-6 border-b border-[#F1F5F9]">
            <Step n={1} title="Prepara ZIP" active={step === 1} done={step > 1} />
            <div className="hidden md:block w-8 h-px bg-slate-200" />
            <Step n={2} title="Carica ZIP" active={step === 2} done={step > 2} />
            <div className="hidden md:block w-8 h-px bg-slate-200" />
            <Step n={3} title="Anteprima" active={step === 3} done={step > 3} />
            <div className="hidden md:block w-8 h-px bg-slate-200" />
            <Step n={4} title="Riepilogo" active={step === 4} done={step > 4} />
          </div>

          {/* STEP 1 */}
          {!file && (
            <div>
              <div className="rounded-2xl bg-[#F0FAF3] border border-[#E1F3E8] p-5 mb-6">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-[#12964A] flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-slate-700 space-y-3">
                    <div><strong>Convenzione nomi file (obbligatoria)</strong></div>
                    <div className="grid gap-1.5 font-mono text-xs bg-white rounded-lg p-3 border border-[#E1F3E8]">
                      <div><span className="text-[#12964A]">slug-attivita</span>_cover.jpg &nbsp;→ &nbsp;copertina scheda</div>
                      <div><span className="text-[#12964A]">slug-attivita</span>_01.jpg &nbsp;→ &nbsp;galleria posizione 1</div>
                      <div><span className="text-[#12964A]">slug-attivita</span>_02.jpg &nbsp;→ &nbsp;galleria posizione 2</div>
                      <div><span className="text-[#12964A]">slug-attivita</span>_03.jpg &nbsp;→ &nbsp;galleria posizione 3</div>
                    </div>
                    <div>
                      <strong>Esempio:</strong>{" "}
                      <span className="font-mono text-xs">boutique-elena_cover.jpg</span>,{" "}
                      <span className="font-mono text-xs">boutique-elena_01.jpg</span>
                    </div>
                    <div className="text-xs text-slate-600">
                      Formati ammessi: JPG, JPEG, PNG, WEBP · Max 15 MB per foto · Max 200 MB per ZIP · Max 500 file.
                      Le immagini oltre 2 MB vengono compresse automaticamente (JPEG q=85, larghezza max 2000px).
                    </div>
                  </div>
                </div>
              </div>

              <label className="block border-2 border-dashed border-[#E5E7EB] hover:border-[#12964A] rounded-2xl p-10 text-center cursor-pointer transition-colors">
                <FileArchive className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <div className="text-sm text-slate-600">
                  Trascina qui il file ZIP oppure <span className="text-[#12964A] font-medium">clicca per selezionare</span>
                </div>
                <div className="text-xs text-slate-400 mt-2">Formato supportato: .zip</div>
                <input
                  type="file" accept=".zip,application/zip,application/x-zip-compressed"
                  className="hidden" data-testid="zip-file-input"
                  onChange={(e) => setFile(e.target.files?.[0])}
                />
              </label>

              <div className="mt-4 text-center">
                <a
                  href={adminApi.importImagesZipExampleUrl()}
                  className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-[#12964A] underline"
                  data-testid="zip-example-download"
                >
                  Scarica ZIP di esempio (2 attività demo)
                </a>
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {file && !preview && !result && (
            <div>
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl mb-6">
                <FileArchive className="w-6 h-6 text-[#12964A]" />
                <div className="flex-1">
                  <div className="font-medium text-[#1F2937]">{file.name}</div>
                  <div className="text-xs text-slate-500">{(file.size / (1024 * 1024)).toFixed(2)} MB</div>
                </div>
                <button onClick={reset} className="text-xs text-red-600 hover:underline" data-testid="zip-change-file">Cambia file</button>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 mb-2">Modalità galleria</div>
                  <div className="flex flex-col gap-2">
                    <label className="flex items-start gap-3 p-3 rounded-xl border border-[#E5E7EB] cursor-pointer hover:border-[#12964A]">
                      <input type="radio" checked={galleryMode === "append"} onChange={() => setGalleryMode("append")} data-testid="opt-gallery-append" />
                      <div className="text-sm">
                        <div className="font-medium">Aggiungi alla galleria esistente</div>
                        <div className="text-xs text-slate-500">Le nuove foto vengono accodate a quelle già presenti.</div>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 p-3 rounded-xl border border-[#E5E7EB] cursor-pointer hover:border-[#12964A]">
                      <input type="radio" checked={galleryMode === "replace"} onChange={() => setGalleryMode("replace")} data-testid="opt-gallery-replace" />
                      <div className="text-sm">
                        <div className="font-medium">Sostituisci la galleria</div>
                        <div className="text-xs text-slate-500">Le foto attuali vengono rimosse dalla scheda e sostituite dalle nuove.</div>
                      </div>
                    </label>
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 mb-2">Copertina</div>
                  <div className="flex flex-col gap-2">
                    <label className="flex items-start gap-3 p-3 rounded-xl border border-[#E5E7EB] cursor-pointer hover:border-[#12964A]">
                      <input type="radio" checked={replaceCover === true} onChange={() => setReplaceCover(true)} data-testid="opt-cover-replace" />
                      <div className="text-sm">
                        <div className="font-medium">Sostituisci sempre</div>
                        <div className="text-xs text-slate-500">Se nel ZIP è presente <span className="font-mono">*_cover</span>, sovrascrive quella attuale.</div>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 p-3 rounded-xl border border-[#E5E7EB] cursor-pointer hover:border-[#12964A]">
                      <input type="radio" checked={replaceCover === false} onChange={() => setReplaceCover(false)} data-testid="opt-cover-keep" />
                      <div className="text-sm">
                        <div className="font-medium">Mantieni se esistente</div>
                        <div className="text-xs text-slate-500">Imposta la copertina solo se l&apos;attività non ne ha già una.</div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <button
                onClick={doPreview}
                disabled={previewing}
                data-testid="zip-preview-btn"
                className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] disabled:opacity-50 text-white rounded-full px-6 py-3 text-sm"
              >
                {previewing ? (<><Loader2 className="w-4 h-4 animate-spin" /> Analisi in corso…</>) : (<>Analizza ZIP →</>)}
              </button>
            </div>
          )}

          {/* STEP 3 — preview */}
          {preview && !result && (
            <div data-testid="zip-preview-panel">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                <Stat n={preview.matched_count} label="Abbinate" color="green" />
                <Stat n={preview.orphans_count} label="Orfane" color="yellow" />
                <Stat n={preview.duplicates_count} label="Duplicate" color="yellow" />
                <Stat n={preview.oversized_count} label="Troppo grandi" color="red" />
                <Stat n={preview.invalid_count} label="Nome non valido" color="red" />
              </div>

              <div className="text-xs text-slate-500 mb-4">
                {preview.businesses_affected} attività coinvolte · Compressione automatica a partire da {preview.settings?.compress_threshold_mb} MB
              </div>

              {matchedByBusiness.length > 0 && (
                <div className="border border-[#E1F3E8] rounded-2xl overflow-hidden mb-4">
                  <div className="bg-[#F0FAF3] px-4 py-2 text-xs uppercase tracking-[0.15em] text-[#0E7C3D] font-medium">
                    Immagini abbinate ({preview.matched_count})
                  </div>
                  <div className="max-h-[380px] overflow-y-auto divide-y divide-[#F1F5F9]" data-testid="matched-list">
                    {matchedByBusiness.map((biz) => (
                      <div key={biz.slug} className="p-4">
                        <div className="font-medium text-[#1F2937] mb-1">{biz.business_name}</div>
                        <div className="text-xs text-slate-500 font-mono mb-2">{biz.slug}</div>
                        <div className="flex flex-wrap gap-2">
                          {biz.items.map((m) => (
                            <div key={m.filename} className="inline-flex items-center gap-2 text-xs bg-white border border-[#E5E7EB] rounded-full px-3 py-1">
                              <CheckCircle2 className="w-3.5 h-3.5 text-[#12964A]" />
                              <span className="font-mono">{m.filename}</span>
                              <span className="text-slate-400">·</span>
                              <span className="text-slate-600">{m.role_label}</span>
                              <span className="text-slate-400">·</span>
                              <span className="text-slate-500">{m.size_kb} KB</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {preview.orphans?.length > 0 && (
                <IssueList title={`File orfani (${preview.orphans_count})`} icon={<AlertTriangle className="w-4 h-4 text-amber-500" />} items={preview.orphans} testid="orphans-list" />
              )}
              {preview.duplicates?.length > 0 && (
                <IssueList title={`File duplicati (${preview.duplicates_count})`} icon={<AlertTriangle className="w-4 h-4 text-amber-500" />} items={preview.duplicates} testid="duplicates-list" />
              )}
              {preview.oversized?.length > 0 && (
                <IssueList title={`File troppo grandi (${preview.oversized_count})`} icon={<XCircle className="w-4 h-4 text-red-500" />} items={preview.oversized} testid="oversized-list" />
              )}
              {preview.invalid?.length > 0 && (
                <IssueList title={`Nomi file non validi (${preview.invalid_count})`} icon={<XCircle className="w-4 h-4 text-red-500" />} items={preview.invalid} testid="invalid-list" />
              )}

              <div className="flex flex-wrap gap-2 items-center mt-4">
                <button onClick={reset} className="text-sm text-slate-500 hover:text-slate-700" data-testid="zip-reload-btn">← Ricarica ZIP</button>
                <div className="flex-1" />
                <button
                  onClick={doCommit}
                  disabled={committing || preview.matched_count === 0}
                  data-testid="zip-commit-btn"
                  className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] disabled:opacity-50 text-white rounded-full px-6 py-3 text-sm"
                >
                  {committing ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Caricamento in corso…</>
                  ) : (
                    <>Conferma caricamento ({preview.matched_count} immagini) →</>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* STEP 4 */}
          {result && (
            <div data-testid="zip-result-panel">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <Stat n={result.uploaded_count} label="Caricate" color="green" />
                <Stat n={result.updates_applied} label="Schede aggiornate" color="blue" />
                <Stat n={result.orphans_count} label="Orfane" color="yellow" />
                <Stat n={result.errors_count} label="Errori" color="red" />
              </div>
              <div className="rounded-2xl bg-[#F0FAF3] border border-[#E1F3E8] p-5 mb-6">
                <div className="flex items-center gap-2 text-[#0E7C3D] font-medium mb-1">
                  <CheckCircle2 className="w-5 h-5" /> Import ZIP completato
                </div>
                <p className="text-sm text-slate-600">
                  Le immagini sono state caricate su Supabase Storage e associate alle schede.
                  Verifica il risultato sulla pagina pubblica delle attività o dal <Link to="/admin" className="text-[#12964A] underline">pannello attività</Link>.
                </p>
              </div>

              {result.upload_errors?.length > 0 && (
                <IssueList title={`Errori upload (${result.errors_count})`} icon={<XCircle className="w-4 h-4 text-red-500" />} items={result.upload_errors.map(e => ({ filename: e.filename, reason: e.error }))} testid="upload-errors-list" />
              )}

              <button onClick={reset} className="inline-flex items-center gap-2 border border-[#E5E7EB] rounded-full px-5 py-2 text-sm hover:border-[#12964A]" data-testid="zip-new-import">
                Nuova importazione
              </button>
            </div>
          )}

          {error && (
            <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="zip-error">
              {error}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

const IssueList = ({ title, icon, items, testid }) => (
  <div className="border border-[#E5E7EB] rounded-2xl overflow-hidden mb-4" data-testid={testid}>
    <div className="bg-slate-50 px-4 py-2 text-xs uppercase tracking-[0.15em] text-slate-600 font-medium flex items-center gap-2">
      {icon} {title}
    </div>
    <div className="max-h-[220px] overflow-y-auto divide-y divide-[#F1F5F9]">
      {items.map((it, i) => (
        <div key={i} className="px-4 py-2 text-sm flex items-center gap-3">
          <span className="font-mono text-xs text-slate-700 flex-shrink-0">{it.filename}</span>
          <span className="text-slate-400">·</span>
          <span className="text-slate-600 text-xs">{it.reason}</span>
        </div>
      ))}
    </div>
  </div>
);

export default ImportImagesZip;
